import { Box, Card, CardContent, Typography, useTheme } from '@mui/material';
import { DrawRequestDetailResponseModel } from '@models/drawRequests/drawRequestDetailResponseModel';
import FormattedCurrency from '@helpers/formattedCurrency';

interface BudgetReadOnlyCardsProps {
  drawDetails: DrawRequestDetailResponseModel[];
  budgetItemMap: Map<string, string>;
}

export default function BudgetReadOnlyCards({
  drawDetails,
  budgetItemMap,
}: BudgetReadOnlyCardsProps) {
  const theme = useTheme();
  const renderCardHeader = (index: number) => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2,
      }}
    >
      <Typography
        variant="h6"
        component="h3"
        sx={{
          color: theme.palette.primary.main,
        }}
        aria-label={`Budget item ${index + 1}`}
      >
        Item #{index + 1}
      </Typography>
    </Box>
  );

  const renderFieldLabel = (label: string, fieldId: string) => (
    <Typography
      variant="body2"
      component="label"
      htmlFor={fieldId}
      sx={{
        mb: 1,
      }}
    >
      {label}
    </Typography>
  );

  const renderFieldValue = (children: React.ReactNode, fieldId: string, ariaLabel: string) => (
    <Typography
      variant="body1"
      id={fieldId}
      role="text"
      aria-label={ariaLabel}
      sx={{
        backgroundColor: theme.palette.grey[100],
        padding: '8px 12px',
        border: `1px solid ${theme.palette.divider}`,
        minHeight: '40px',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      {children}
    </Typography>
  );

  const renderBudgetItemField = (detail: any, index: number) => {
    const fieldId = `budget-item-${index}`;
    const budgetItemName = budgetItemMap.get(detail.budgetItemRecId) || detail.budgetItem || 'Budget Item';
    return (
      <Box>
        {renderFieldLabel('Budget Item', fieldId)}
        {renderFieldValue(
          budgetItemName,
          fieldId,
          `Budget item: ${budgetItemName}`
        )}
      </Box>
    );
  };

  const renderRemainingBudgetField = (detail: any, index: number) => {
    const fieldId = `remaining-budget-${index}`;
    return (
      <Box>
        {renderFieldLabel('Remaining Budget', fieldId)}
        {renderFieldValue(
          <FormattedCurrency value={detail.remainingBudget} />,
          fieldId,
          `Remaining budget: ${detail.remainingBudget}`
        )}
      </Box>
    );
  };

  const renderRequestField = (detail: any, index: number) => {
    const fieldId = `request-amount-${index}`;
    return (
      <Box>
        {renderFieldLabel('Request', fieldId)}
        {renderFieldValue(
          <FormattedCurrency value={detail.drawAmount} />,
          fieldId,
          `Request amount: ${detail.drawAmount}`
        )}
      </Box>
    );
  };

  const renderPercentCompleteField = (detail: any, index: number) => {
    const fieldId = `percent-complete-${index}`;
    return (
      <Box>
        {renderFieldLabel('% Complete', fieldId)}
        {renderFieldValue(
          `${detail.pctCompleted}%`,
          fieldId,
          `Percent complete: ${detail.pctCompleted}%`
        )}
      </Box>
    );
  };

  const renderCardFields = (detail: any, index: number) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {renderBudgetItemField(detail, index)}
      {renderRemainingBudgetField(detail, index)}
      {renderRequestField(detail, index)}
      {renderPercentCompleteField(detail, index)}
    </Box>
  );

  const renderCard = (detail: any, index: number) => (
    <Card
      key={detail.recId || index}
      sx={{
        borderRadius: '12px',
        boxShadow: theme.shadows[2],
        border: `1px solid ${theme.palette.divider}`,
      }}
      component="article"
      role="article"
      aria-label={`Budget item ${index + 1} details`}
    >
      <CardContent sx={{ p: 3 }}>
        {renderCardHeader(index)}
        {renderCardFields(detail, index)}
      </CardContent>
    </Card>
  );

  const renderCardsList = () => (
    <Box 
      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      role="group"
      aria-label="Budget items list"
    >
      {drawDetails.map((detail, index) => renderCard(detail, index))}
    </Box>
  );

  return renderCardsList();
}
